import { createAsyncThunk, createAction, createReducer } from '@reduxjs/toolkit';
import { API, SCOPE_CRM_CONTACTS } from '../../constants';
import { ANY_OF_THOSE } from '../../constants/filter';
import { application } from '../../services/application';
import { INITIAL_LIST_STATE_EXTENDED } from '../helpers/common';
import { extendListBuilder, generateListActions } from '../helpers/listActionsHelpers';
import { extendBuilderWithFacetCounts, extendBuilderWithListControls } from '../helpers/sharedCases';
import { getFilterParams } from '../../utils/filterUtils';

const FIELD_FILTER_KEYS = [
  'status',
  'contactType',
  'stage',
  'disposition',
  'source',
  'origin',
  'assignedTo',
  'crmCompanyId',
  'createdAt',
  'touchedSince',
  'tags',
  'createdWeek',
  'lastContactedWeek',
];

export const INITIAL_STATE = {
  ...INITIAL_LIST_STATE_EXTENDED,
  order: ['lastName', 'asc'],
  filters: {
    text: '',
    fields: {
      status: { data: ['active'], type: ANY_OF_THOSE },
    },
  },
  selectedContact: null,
  contactPending: false,
  savePending: false,
  importPending: false,
  summaryCounts: {
    vendors: 0,
    agents: 0,
    organizations: 0,
    newContacts: 0,
  },
  summaryCountsPending: false,
  summaryCountsLoaded: false,
  facetCounts: null,
  facetCountsPending: false,
  facetCountsRequestId: null,
  // Kanban board uses its own full unpaginated list so it never shows a partial page
  kanbanItems: [],
  kanbanPending: false,
  kanbanRefreshing: false,
  kanbanLoadFailed: false,
  kanbanRequestId: null,
};

const listActions = generateListActions({
  scope: SCOPE_CRM_CONTACTS,
  apiMethod: {
    GET_LIST: API.CRM_CONTACTS.GET_LIST,
  },
  getStore: (store) => store.crmContacts,
});

export const clearSelectedContact = createAction(`${SCOPE_CRM_CONTACTS}/clearSelectedContact`);
export const setContactListFilters = createAction(`${SCOPE_CRM_CONTACTS}/setContactListFilters`);
export const setContactListOrder = createAction(`${SCOPE_CRM_CONTACTS}/setContactListOrder`);
export const setContactListPage = createAction(`${SCOPE_CRM_CONTACTS}/setContactListPage`);
export const setContactListPerPage = createAction(`${SCOPE_CRM_CONTACTS}/setContactListPerPage`);

export const fetchContact = createAsyncThunk(`${SCOPE_CRM_CONTACTS}/fetchContact`, (params) => {
  return application.call(API.CRM_CONTACTS.FIND_ONE, params);
});

export const createContact = createAsyncThunk(`${SCOPE_CRM_CONTACTS}/createContact`, (params) => {
  return application.call(API.CRM_CONTACTS.CREATE, params);
});

export const updateContact = createAsyncThunk(`${SCOPE_CRM_CONTACTS}/updateContact`, (params) => {
  return application.call(API.CRM_CONTACTS.UPDATE, params);
});

export const bulkUpdateContacts = createAsyncThunk(`${SCOPE_CRM_CONTACTS}/bulkUpdateContacts`, (params) => {
  return application.call(API.CRM_CONTACTS.BULK_UPDATE, params);
});

export const importContactsCsv = createAsyncThunk(`${SCOPE_CRM_CONTACTS}/importContactsCsv`, (params) => {
  return application.call(API.CRM_CONTACTS.IMPORT_CSV, params);
});

export const fetchSummaryCounts = createAsyncThunk(`${SCOPE_CRM_CONTACTS}/fetchSummaryCounts`, () => {
  return application.call(API.CRM_CONTACTS.GET_SUMMARY_COUNTS);
});

export const fetchFacetCounts = createAsyncThunk(
  `${SCOPE_CRM_CONTACTS}/fetchFacetCounts`,
  (activityWindow, { getState }) => {
    const { filters } = getState().crmContacts;
    const { text, fields } = filters;

    return application.call(API.CRM_CONTACTS.GET_FACET_COUNTS, {
      text: text || '',
      filters: getFilterParams(fields),
      activityWindow: activityWindow || undefined,
    });
  }
);

// Fetches ALL active contacts for the Kanban board by paginating through
// the GET_LIST endpoint at the maximum allowed perPage (100) and concatenating
// pages until every contact is loaded. Uses its own state slice (kanbanItems)
// so it never clobbers the paginated table list.
const KANBAN_PER_PAGE = 100;

export const fetchKanbanContacts = createAsyncThunk(
  `${SCOPE_CRM_CONTACTS}/fetchKanbanContacts`,
  async (_, { getState }) => {
    const { filters, order } = getState().crmContacts;
    const { text, fields } = filters;
    const serializedFilters = getFilterParams(fields);

    const baseParams = {
      text: text || '',
      filters: serializedFilters,
      order,
    };

    // First page
    const firstPage = await application.call(API.CRM_CONTACTS.GET_LIST, {
      ...baseParams,
      pagination: { page: 1, perPage: KANBAN_PER_PAGE },
    });

    const allItems = [...(firstPage.items || [])];
    const totalCount = firstPage.count || 0;
    const totalPages = Math.ceil(totalCount / KANBAN_PER_PAGE);

    // Fetch remaining pages in parallel
    if (totalPages > 1) {
      const pagePromises = [];
      for (let page = 2; page <= totalPages; page++) {
        pagePromises.push(
          application.call(API.CRM_CONTACTS.GET_LIST, {
            ...baseParams,
            pagination: { page, perPage: KANBAN_PER_PAGE },
          })
        );
      }
      const pageResults = await Promise.all(pagePromises);
      pageResults.forEach((result) => {
        allItems.push(...(result.items || []));
      });
    }

    return { items: allItems, count: totalCount };
  }
);

export const actions = {
  ...listActions,
  clearSelectedContact,
  setContactListFilters,
  setContactListOrder,
  setContactListPage,
  setContactListPerPage,
  fetchContact,
  createContact,
  updateContact,
  bulkUpdateContacts,
  importContactsCsv,
  fetchSummaryCounts,
  fetchFacetCounts,
  fetchKanbanContacts,
};

export default createReducer(INITIAL_STATE, (builder) => {
  extendListBuilder(builder, actions);

  extendBuilderWithListControls(
    builder,
    {
      setFilters: setContactListFilters,
      setOrder: setContactListOrder,
      setPage: setContactListPage,
      setPerPage: setContactListPerPage,
    },
    FIELD_FILTER_KEYS
  );

  builder
    .addCase(fetchContact.pending, (state) => {
      state.contactPending = true;
    })
    .addCase(fetchContact.fulfilled, (state, action) => {
      state.selectedContact = action.payload;
      state.contactPending = false;
    })
    .addCase(fetchContact.rejected, (state) => {
      state.selectedContact = null;
      state.contactPending = false;
    })
    .addCase(createContact.pending, (state) => {
      state.savePending = true;
    })
    .addCase(createContact.fulfilled, (state, action) => {
      state.savePending = false;
      const contact = action.payload?.contact;

      if (contact) {
        state.items.unshift(contact);
        state.count += 1;

        if (typeof state.totalCount === 'number') {
          state.totalCount += 1;
        }

        if (typeof state.allCount === 'number') {
          state.allCount += 1;
        }

        if (typeof state.total === 'number') {
          state.total += 1;
        }
      }
    })
    .addCase(createContact.rejected, (state) => {
      state.savePending = false;
    })
    .addCase(updateContact.pending, (state) => {
      state.savePending = true;
    })
    .addCase(updateContact.fulfilled, (state, action) => {
      state.savePending = false;
      const contact = action.payload?.contact;

      if (contact) {
        state.selectedContact = contact;
        const index = state.items.findIndex((item) => item.id === contact.id);
        if (index >= 0) {
          state.items[index] = contact;
        }

        // Keep the Kanban board in sync without forcing a full reload/spinner flash
        const kanbanIndex = state.kanbanItems.findIndex((item) => item.id === contact.id);
        if (kanbanIndex >= 0) {
          state.kanbanItems[kanbanIndex] = { ...state.kanbanItems[kanbanIndex], ...contact };
        }
      }
    })
    .addCase(updateContact.rejected, (state) => {
      state.savePending = false;
    })
    .addCase(importContactsCsv.pending, (state) => {
      state.importPending = true;
    })
    .addCase(importContactsCsv.fulfilled, (state, action) => {
      state.importPending = false;
      const importedCount = action.payload?.imported || 0;

      if (importedCount > 0) {
        state.pagination.page = 1;
      }
    })
    .addCase(importContactsCsv.rejected, (state) => {
      state.importPending = false;
    })
    .addCase(clearSelectedContact, (state) => {
      state.selectedContact = null;
      state.contactPending = false;
      state.savePending = false;
    })
    .addCase(fetchKanbanContacts.pending, (state, action) => {
      // Only show the loading spinner on the initial Kanban load.
      // Subsequent refreshes (e.g. after a stage move) keep the cards on screen under the refresh bar.
      state.kanbanRequestId = action.meta.requestId;
      state.kanbanLoadFailed = false;
      if (state.kanbanItems.length) {
        state.kanbanRefreshing = true;
      } else {
        state.kanbanPending = true;
      }
    })
    .addCase(fetchKanbanContacts.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.kanbanRequestId) return;
      state.kanbanPending = false;
      state.kanbanRefreshing = false;
      state.kanbanItems = action.payload?.items || [];
    })
    .addCase(fetchKanbanContacts.rejected, (state, action) => {
      if (action.meta.requestId !== state.kanbanRequestId) return;
      state.kanbanPending = false;
      state.kanbanRefreshing = false;
      state.kanbanLoadFailed = true;
    })
    .addCase(fetchSummaryCounts.pending, (state) => {
      state.summaryCountsPending = true;
    })
    .addCase(fetchSummaryCounts.fulfilled, (state, action) => {
      state.summaryCounts = action.payload || { vendors: 0, agents: 0, organizations: 0, newContacts: 0 };
      state.summaryCountsPending = false;
      state.summaryCountsLoaded = true;
    })
    .addCase(fetchSummaryCounts.rejected, (state) => {
      state.summaryCountsPending = false;
    });
  extendBuilderWithFacetCounts(builder, fetchFacetCounts);

  return builder;
});
